import React from "react"
import { Modal, Box, Typography, Button } from "@mui/material"
import Wifi from "@mui/icons-material/Wifi"
import WifiOff from "@mui/icons-material/WifiOff"

interface ICommonModalProps {
    open: boolean // Indicates whether the modal is open
    handleClose: () => void // Function to handle closing the modal
    title?: string // Title of the modal
    description?: string // Description content in the modal
    icon?: React.ReactNode // Optional React node for an icon
    isLoading?: boolean // Optional boolean to show a loading state
    buttonText?: string // Optional text for the button
    showButton?: boolean // Optional boolean to show or hide the button, default is true
}

const CommonModal = ({
    open,
    handleClose,
    title,
    description,
    icon,
    buttonText,
    showButton = true,
}: ICommonModalProps) => {
    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            BackdropProps={{
                // This will prevent closing the modal when clicking outside
                onClick: (e) => e.stopPropagation(),
            }}
        >
            <Box
                sx={{
                    width: 350,
                    bgcolor: "white",
                    borderRadius: 3,
                    p: 4,
                    textAlign: "center",
                    animation: "fadeIn 0.5s",
                    "@keyframes fadeIn": {
                        "0%": { opacity: 0, transform: "scale(0.9)" },
                        "100%": { opacity: 1, transform: "scale(1)" },
                    },
                }}
            >
                {/* Icon */}
                {icon && <Box sx={{ mb: 2 }}>{icon}</Box>}

                {/* Title */}
                <Typography
                    id="modal-title"
                    variant="h5"
                    component="h2"
                    sx={{ fontWeight: "bold", color: "#333", mb: 1 }}
                >
                    {title}
                </Typography>

                {/* Description */}
                <Typography
                    id="modal-description"
                    sx={{
                        fontSize: "1rem",
                        color: "#555",
                        lineHeight: 1.5,
                        mb: 3,
                    }}
                >
                    {description}
                </Typography>

                {showButton && (
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{
                            mt: 2,
                            px: 4,
                            borderRadius: 2,
                        }}
                        onClick={handleClose}
                    >
                        {buttonText}
                    </Button>
                )}
            </Box>
        </Modal>
    )
}

// Usage for OfflineModal
const OfflineModal = ({ open, handleClose }: ICommonModalProps) => {
    return (
        <CommonModal
            open={open}
            handleClose={handleClose}
            title="Oops! You're Offline"
            description="We noticed that your internet connection is unavailable. Don't worry! You can keep working, and your updates will sync automatically when you're back online."
            icon={<WifiOff sx={{ fontSize: 60, color: "#ff5252" }} />}
            buttonText="Got it!"
            showButton={true}
        />
    )
}

// Usage for OnlineModal
const OnlineModal = ({ open, isLoading, handleClose }: ICommonModalProps) => {
    return (
        <CommonModal
            open={open}
            handleClose={handleClose}
            title="Back Online!"
            description="Great news! You're reconnected to the internet. We’re syncing your changes to ensure everything is up to date."
            icon={<Wifi sx={{ fontSize: 60, color: "#4caf50" }} />}
            buttonText="Done"
            showButton={isLoading}
        />
    )
}

interface IStorageWarningModalProps {
    open: boolean
    title: string
    description: string
    storageCritical: boolean
    onClose: () => void
}

const StorageWarningModal = ({ open, title, description, storageCritical, onClose }: IStorageWarningModalProps) => {
    return (
        <CommonModal
            open={open}
            handleClose={onClose}
            title={title}
            description={description}
            icon={<WifiOff sx={{ fontSize: 60, color: "#ff5252" }} />}
            buttonText={"Got it!"}
            showButton={!storageCritical}
        />
    )
}

export { OfflineModal, OnlineModal, StorageWarningModal }
